import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { APP_INITIALIZER, ApplicationConfig, inject } from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading
} from "@angular/router";
import { provideFuse } from "@fuse";
import { TranslocoService, provideTransloco } from "@ngneat/transloco";
import { appRoutes } from "app/app.routes";
import { TokenStorageService } from "app/core/auth/services";
import { resolveTenantClientId } from "app/core/auth/tenant-client.util";
import {
  authInterceptor,
  httpErrorInterceptor,
} from "app/core/auth/interceptors";
import { provideIcons } from "app/core/icons/icons.provider";
import { mockApiServices } from "app/mock-api";
import { environment } from "environments/environment";
import { KeycloakService } from "keycloak-angular";
import { provideToastr } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { TranslocoHttpLoader } from "./core/services/transloco.http-loader";

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideToastr(),
    provideHttpClient(withInterceptors([authInterceptor, httpErrorInterceptor])),

    // keycloak-angular@16 (última versión compatible con Angular 18) no expone
    // provideKeycloak() -- esa API standalone llegó recién en la v19, que pide
    // Angular 19+. Inicializamos KeycloakService vía APP_INITIALIZER, con el
    // mismo patrón que ya se usaba acá abajo para precargar Transloco.
    // KeycloakService NO tiene providedIn:'root' (se registraba vía
    // KeycloakAngularModule/CoreModule en apps con NgModules) así que en una
    // app standalone hay que darlo de alta a mano o el injector lo rechaza.
    KeycloakService,
    {
      provide: APP_INITIALIZER,
      useFactory:
        (keycloak: KeycloakService, tokenStorage: TokenStorageService) =>
        async () => {
          // Los tokens guardados en la sesión anterior de la pestaña son la vía
          // principal para recuperar la sesión: keycloak.init() los renueva vía
          // refresh_token. El check-sso por cookie queda sólo como camino para
          // la primera carga (sin tokens guardados), porque este Keycloak
          // responde login_required aunque la sesión de usuario esté viva.
          const stored = tokenStorage.load();

          await keycloak.init({
            config: {
              url: environment.KEYCLOAK_URL,
              realm: environment.KEYCLOAK_REALM,
              // El clientId sale del sistema de tenants existente (mismo host ->
              // DOMAIN_CLIENT_ID que ya usan FolderService/ThemeService).
              clientId: resolveTenantClientId(),
            },
            initOptions: {
              token: stored?.token,
              refreshToken: stored?.refreshToken,
              idToken: stored?.idToken,
              onLoad: "check-sso",
              silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
              pkceMethod: "S256",
              // El sondeo periódico de estado de sesión usa el mismo mecanismo
              // de cookie que ya sabemos que no funciona acá: sólo agregaría
              // round-trips que siempre fallan.
              checkLoginIframe: false,
            },
            // No usamos el interceptor/guard propios de la librería: authInterceptor
            // y authGuard (core/auth) dan control fino sobre hosts y headers custom.
            enableBearerInterceptor: false,
            loadUserProfileAtStartUp: false,
          });

          const instance = keycloak.getKeycloakInstance();

          const persist = () => {
            if (instance.token && instance.refreshToken) {
              tokenStorage.save({
                token: instance.token,
                refreshToken: instance.refreshToken,
                idToken: instance.idToken,
              });
            }
          };

          // init() ya emitió onAuthSuccess antes de que pudiéramos engancharnos,
          // así que se guarda una vez acá y después en cada renovación.
          persist();
          instance.onAuthSuccess = persist;
          instance.onAuthRefreshSuccess = persist;
          instance.onAuthLogout = () => tokenStorage.clear();
          instance.onAuthRefreshError = () => tokenStorage.clear();
        },
      deps: [KeycloakService, TokenStorageService],
      multi: true,
    },

    provideRouter(
      appRoutes,
      // withViewTransitions({ skipInitialTransition: true }),
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "L",
        },
        display: {
          dateInput: "dd/MM/yyyy",
          monthYearLabel: "LLL yyyy",
          dateA11yLabel: "DD",
          monthYearA11yLabel: "LLLL yyyy",
        },
      },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: "es",
            label: "Español",
          },
          {
            id: "en",
            label: "English",
          },
        ],
        defaultLang: "es",
        fallbackLang: "es",
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(defaultLang);

        return () => firstValueFrom(translocoService.load(defaultLang));
      },
      multi: true,
    },

    // Fuse
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: "futuristic",
        scheme: "dark",
        screens: {
          sm: "600px",
          md: "960px",
          lg: "1280px",
          xl: "1440px",
        },
        theme: "theme-default",
        themes: [
          {
            id: "theme-default",
            name: "Default",
          },
          {
            id: "theme-brand",
            name: "Brand",
          },
          {
            id: "theme-teal",
            name: "Teal",
          },
          {
            id: "theme-rose",
            name: "Rose",
          },
          {
            id: "theme-purple",
            name: "Purple",
          },
          {
            id: "theme-amber",
            name: "Amber",
          },
        ],
      },
    }),
  ],
};
